import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ArrowRight,
  Award,
  BookOpenCheck,
  Clock,
  GraduationCap,
  ListChecks,
  PlayCircle,
  Target,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert, EmptyState } from '@/components/ui/Alert';
import { Badge, ProgressBadge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, StatTile } from '@/components/ui/Card';
import { ProgressBar, ProgressRing } from '@/components/ui/Progress';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { formatPercent, relativeTime } from '@/lib/format';
import { fetchers, queryKeys } from '@/services/queries';

export function DashboardPage() {
  const dashboard = useQuery({ queryKey: queryKeys.dashboard, queryFn: fetchers.dashboard });

  if (dashboard.isLoading) return <FullPageSpinner label="Chargement de votre tableau de bord…" />;
  if (dashboard.isError || !dashboard.data) {
    return (
      <Alert tone="danger" title="Tableau de bord indisponible">
        Les données de progression n'ont pas pu être récupérées. Réessayez dans un instant.
      </Alert>
    );
  }

  const data = dashboard.data;
  const firstName = data.full_name.split(' ')[0] ?? data.full_name;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bonjour {firstName}</h1>
          <p className="mt-1 text-sm text-muted">
            {data.modules_available > 0
              ? `${data.modules_completed} module${data.modules_completed > 1 ? 's' : ''} terminé${
                  data.modules_completed > 1 ? 's' : ''
                } sur ${data.modules_available} accessible${data.modules_available > 1 ? 's' : ''}.`
              : "Vous n'êtes pas encore inscrit à un parcours."}
          </p>
        </div>
        {data.continue_learning ? (
          <ButtonLink
            to={`/app/modules/${data.continue_learning.module_code}/chapitres/${data.continue_learning.chapter_slug}`}
          >
            <PlayCircle className="h-4 w-4" />
            Reprendre
          </ButtonLink>
        ) : null}
      </header>

      {/* Key figures — every value is computed by the backend. */}
      <section
        aria-label="Indicateurs de progression"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatTile
          label="Progression globale"
          value={`${data.overall_percent} %`}
          hint={`${data.chapters_completed} chapitre${data.chapters_completed > 1 ? 's' : ''} terminé${data.chapters_completed > 1 ? 's' : ''}`}
          icon={<Target className="h-4 w-4" />}
          accent="tech"
        />
        <StatTile
          label="Modules terminés"
          value={`${data.modules_completed}/${data.modules_available}`}
          hint={`${data.hours_enrolled} h de formation inscrites`}
          icon={<BookOpenCheck className="h-4 w-4" />}
          accent="ai"
        />
        <StatTile
          label="Quiz réussis"
          value={data.quizzes_passed}
          hint={`${data.pending_quizzes.length} quiz à réaliser`}
          icon={<ListChecks className="h-4 w-4" />}
          accent="agent"
        />
        <StatTile
          label="Score moyen"
          value={formatPercent(data.average_score_percent)}
          hint="Calculé sur vos tentatives"
          icon={<Award className="h-4 w-4" />}
          accent="neutral"
        />
      </section>

      {data.tracks.length === 0 ? (
        <EmptyState
          icon={<GraduationCap className="mx-auto h-8 w-8" />}
          title="Aucun parcours accessible"
          description="Votre compte n'est inscrit à aucun parcours. Un administrateur doit vous inscrire pour que les cours apparaissent ici."
          action={
            <ButtonLink to="/parcours" variant="outline">
              Voir le catalogue
            </ButtonLink>
          }
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {data.continue_learning ? (
              <Card className="overflow-hidden">
                <div className="bg-gradient-to-r from-tech/10 to-ai/10 px-5 py-5">
                  <p className="text-2xs font-semibold uppercase tracking-wide text-tech">
                    Cours en cours
                  </p>
                  <h2 className="mt-2 text-lg font-bold text-fg">
                    {data.continue_learning.chapter_title}
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    {data.continue_learning.module_code} — {data.continue_learning.module_title}
                  </p>
                  <ButtonLink
                    to={`/app/modules/${data.continue_learning.module_code}/chapitres/${data.continue_learning.chapter_slug}`}
                    className="mt-4"
                    size="sm"
                  >
                    Continuer la lecture
                    <ArrowRight className="h-4 w-4" />
                  </ButtonLink>
                </div>
              </Card>
            ) : null}

            {data.tracks.map((track) => (
              <Card key={track.track_id}>
                <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle as="h2">{track.track_title}</CardTitle>
                    <p className="mt-0.5 text-xs text-faint">
                      {track.modules_completed}/{track.modules_total} modules · {track.hours_total} h
                    </p>
                  </div>
                  <Badge
                    tone={
                      track.track_code === 'A' ? 'tech' : track.track_code === 'B' ? 'ai' : 'agent'
                    }
                  >
                    Parcours {track.track_code}
                  </Badge>
                </CardHeader>
                <CardBody>
                  <ProgressBar
                    value={track.percent}
                    label="Progression du parcours"
                    tone={
                      track.track_code === 'A' ? 'tech' : track.track_code === 'B' ? 'ai' : 'agent'
                    }
                  />
                  {track.modules.length === 0 ? (
                    <p className="mt-4 text-sm text-muted">
                      Aucun module de ce parcours n'est encore ouvert.
                    </p>
                  ) : (
                    <ul className="mt-5 space-y-3">
                      {track.modules.map((module) => (
                        <li
                          key={module.module_id}
                          className="rounded-lg border border-border bg-surface-2/50 p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-fg">
                                <span className="font-mono text-xs text-tech">
                                  {module.module_code}
                                </span>{' '}
                                {module.module_title}
                              </p>
                              <p className="mt-0.5 text-2xs text-faint">
                                {module.chapters_completed}/{module.chapters_total} chapitres ·{' '}
                                {module.labs_completed}/{module.labs_total} TP ·{' '}
                                {module.quizzes_passed}/{module.quizzes_total} quiz
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <ProgressBadge status={module.status} />
                              <Link
                                to={`/modules/${module.module_code}`}
                                className="text-xs font-semibold text-tech hover:underline"
                              >
                                Ouvrir
                              </Link>
                            </div>
                          </div>
                          <ProgressBar
                            value={module.percent}
                            showValue={false}
                            size="sm"
                            className="mt-2.5"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>

          <aside className="space-y-6">
            <Card>
              <CardBody className="flex flex-col items-center py-7">
                <ProgressRing value={data.overall_percent} label="global" />
                <p className="mt-4 text-center text-xs text-muted">
                  Progression calculée côté serveur sur les chapitres, TP et quiz des modules
                  ouverts.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-ai" />
                  Quiz à réaliser
                </CardTitle>
              </CardHeader>
              <CardBody className={data.pending_quizzes.length ? 'p-0' : undefined}>
                {data.pending_quizzes.length === 0 ? (
                  <p className="text-sm text-muted">
                    Aucun quiz en attente. Tous les quiz accessibles sont réussis.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.pending_quizzes.map((quiz) => (
                      <li key={quiz.quiz_id} className="px-5 py-3">
                        <Link
                          to={`/app/quiz/${quiz.quiz_id}`}
                          className="group flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-fg group-hover:text-tech">
                              {quiz.quiz_title}
                            </p>
                            <p className="text-2xs text-faint">
                              {quiz.module_code} · tentative {quiz.attempts_used + 1}/
                              {quiz.max_attempts}
                              {quiz.best_score_percent !== null &&
                              quiz.best_score_percent !== undefined
                                ? ` · meilleur ${Math.round(quiz.best_score_percent)} %`
                                : ''}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0 text-faint group-hover:text-tech" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-tech" />
                  Dernières activités
                </CardTitle>
              </CardHeader>
              <CardBody className={data.recent_activity.length ? 'p-0' : undefined}>
                {data.recent_activity.length === 0 ? (
                  <p className="text-sm text-muted">
                    Votre activité apparaîtra ici dès votre premier chapitre.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.recent_activity.map((item) => (
                      <li key={item.id} className="flex items-start gap-3 px-5 py-2.5">
                        <Clock
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint"
                          aria-hidden="true"
                        />
                        <div className="min-w-0">
                          <p className="text-sm text-fg">{item.label}</p>
                          <p className="text-2xs text-faint">{relativeTime(item.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </aside>
        </div>
      )}
    </div>
  );
}
